export const normalizeRentalIds = (ids) => [...new Set((ids || [])
  .map(Number).filter((id) => Number.isSafeInteger(id) && id > 0))];

export function ruleToday(timezone, now = new Date()) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en", {
    timeZone: timezone || "UTC", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(now).map(({ type, value }) => [type, value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

export function propertySelectionError({ ids, taskType, recurring, allRentals = false }) {
  if (taskType === "maintenance" && (allRentals || ids.length !== 1)) {
    return "Maintenance requires exactly one property. Remove extra properties to continue.";
  }
  if (!allRentals && !ids.length && (recurring || taskType !== "other")) {
    return "Select at least one property.";
  }
  return null;
}

export function batchSchedule(schedule, index, consecutive) {
  if (!schedule) return null;
  const minutes = (time) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
  const start = minutes(schedule.scheduled_start_time);
  const duration = minutes(schedule.scheduled_end_time) - start;
  const nextStart = start + (consecutive ? index * duration : 0);
  const nextEnd = nextStart + duration;
  if (duration <= 0 || nextEnd >= 1440) {
    throw new Error("The selected schedules must finish before midnight. Choose an earlier time or a shorter duration.");
  }
  const time = (value) => `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}:00`;
  return { ...schedule, scheduled_start_time: time(nextStart), scheduled_end_time: time(nextEnd) };
}

export const saveErrorMessage = (error) => {
  const body = error?.response?.data;
  return body?.conflicts?.join("; ") || body?.error || error?.message || "Save failed";
};

// Keep acknowledged IDs in the open form: a schedule retry must never POST the
// task again. Uncertain POST outcomes cannot be retried safely without server
// idempotency, so require checking the task list instead of creating duplicates.
export async function savePropertyTasks({ data, rentalIds, schedule, consecutive,
  progress, create, saveSchedule, onCreated = () => {} }) {
  const targets = rentalIds.length ? rentalIds : [null];
  const schedules = targets.map((_, index) => batchSchedule(schedule, index, consecutive));
  const failures = [];
  for (let index = 0; index < targets.length; index += 1) {
    const rentalId = targets[index];
    const key = String(rentalId || "none");
    let entry = progress.get(key);
    if (entry?.uncertain) {
      failures.push({ rentalId, message: "The previous create result is unknown. Check the task list before creating another task." });
      continue;
    }
    try {
      if (!entry?.id) {
        const payload = JSON.parse(JSON.stringify({ ...data, rental_id: rentalId }));
        delete payload.rental_ids;
        delete payload.rental_scope;
        // A reservation/message belongs to one home, never every batch home.
        if (targets.length > 1) {
          payload.guest_reservation_id = null;
          payload.message_id = null;
        }
        let response;
        try {
          response = await create(payload);
        } catch (error) {
          if (!error?.response || error.response.status >= 500) progress.set(key, { uncertain: true });
          throw error;
        }
        const task = response?.data || response;
        if (!task?.id) {
          progress.set(key, { uncertain: true });
          throw new Error("The create result could not be confirmed. Check the task list before trying again.");
        }
        const resolved = task.default_assignment?.assignees || (task.work_order_assignees || []).map(a=>({type:a.assignee_type,id:a.assignee_id,assignment_role:a.assignment_role}));
        entry = { id: task.id, scheduled: false, defaultAssigners: resolved, warnings:task.default_assignment?.warnings || [] };
        progress.set(key, entry);
        onCreated(task);
      }
      if (schedules[index] && !entry.scheduled) {
        await saveSchedule(entry.id, { ...schedules[index], assigners: data.assignment_mode === "property_defaults" ? entry.defaultAssigners : data.assigners || [] });
        entry.scheduled = true;
      }
    } catch (error) {
      failures.push({ rentalId, id: entry?.id, message: saveErrorMessage(error) });
    }
  }
  return { failures, warnings:targets.flatMap(id=>(progress.get(String(id || "none"))?.warnings || []).map(message=>({rentalId:id,message}))), created: targets.filter((id) => progress.get(String(id || "none"))?.id).length,
    scheduled: targets.filter((id) => progress.get(String(id || "none"))?.scheduled).length };
}
